"use client";

import React, { useContext, useEffect, useState } from "react";
import { Flame, Heart, Image as ImageIcon, UserMinus, UserPlus, Users } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { SubscriptionAvatar } from "@/components/ui/subscription-avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { followUser, unfollowUser } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IUserDTO, UserPremiumType } from "types";
interface UserCardProps {
  user: IUserDTO;
}
const UserCard: React.FC<UserCardProps> = ({
  user
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [isFollowing, setIsFollowing] = useState(false);

  // Determine subscription badge styling based on subscription type
  const getSubscriptionBadgeStyles = (type: UserPremiumType) => {
    switch (type) {
      case UserPremiumType.BASIC:
        return "bg-sky-700 hover:bg-sky-800";
      case UserPremiumType.PLUS:
        return "bg-red-600 hover:bg-red-700";
      case UserPremiumType.PRO:
        return "bg-amber-600 hover:bg-amber-700";
      case UserPremiumType.ELITE:
        return "bg-gradient-to-r from-emerald-500 via-amber-600 to-sky-500 animate-gradient";
      case UserPremiumType.ULTIMATE:
        return "bg-gradient-to-r from-[#0268A3] via-red-600 to-amber-500 animate-gradient";
      default:
        return "bg-slate-600 hover:bg-slate-700";
    }
  };
  useEffect(() => {
    if (state.user && user) {
      const isUserFollowing = state.user.following?.some(followingUser => followingUser.userId === user.userId);
      setIsFollowing(!!isUserFollowing);
    }
  }, [state.user, user]);
  const handleFollowToggle = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation when clicking the button

    if (!state.isLoggedIn || !state.user) {
      // Prompt user to log in
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    try {
      if (isFollowing) {
        // Unfollow the user
        const response = await unfollowUser(user.userId);
        if (response.success) {
          setIsFollowing(false);
          dispatch({
            type: AppReducerActionType.SET_USER,
            payload: {
              ...state,
              user: {
                ...state.user,
                following: state.user.following?.filter(followingUser => followingUser.userId !== user.userId)
              }
            }
          });
        } else {
          console.error("Error unfollowing user:", response.error);
        }
      } else {
        // Follow the user
        const response = await followUser(user.userId);
        if (response.success) {
          setIsFollowing(true);
          dispatch({
            type: AppReducerActionType.SET_USER,
            payload: {
              ...state,
              user: {
                ...state.user,
                following: [...(state.user.following || []), {
                  userId: user.userId,
                  username: user.username,
                  avatarUrl: user.avatarUrl,
                  streak: user.streak
                }]
              }
            }
          });
        } else {
          console.error("Error following user:", response.error);
        }
      }
    } catch (error) {
      console.error("Error toggling follow state:", error);
    }
  };
  return <Link href={`/profile/${user.username}`} className="block w-full max-w-sm" passHref data-sentry-element="Link" data-sentry-component="UserCard" data-sentry-source-file="index.tsx">
            <Card className="!bg-slate-800 !border !border-slate-700 !shadow-sm !w-fit" data-sentry-element="Card" data-sentry-source-file="index.tsx">
                <CardContent className="!p-2 md:!p-4 !w-fit" data-sentry-element="CardContent" data-sentry-source-file="index.tsx">
                    {/* Header with Avatar and User Info */}
                    <div className="flex items-center justify-center mb-4">
                        {/* Avatar with Streak Badge */}
                        <div className="relative mr-3">
                            <SubscriptionAvatar userPremiumLevel={user.subscriptionType || UserPremiumType.NONE} avatarUrl={user.avatarUrl} username={user.username} size="lg" data-sentry-element="SubscriptionAvatar" data-sentry-source-file="index.tsx" />
                            <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                                <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                    <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                        <div className="absolute -bottom-1 -right-1 bg-slate-800 rounded-full px-1.5 py-0.5 flex items-center gap-0.5 border border-slate-700">
                                            <Flame className="h-3 w-3 text-rose-500" data-sentry-element="Flame" data-sentry-source-file="index.tsx" />
                                            <span className="text-xs font-bold text-rose-500">
                                                {user.streak}
                                            </span>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                        <p className="text-xs">Login Streak</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>

                        {/* User Info */}
                        <div className="flex flex-col">
                            <h3 className="text-lg font-bold text-slate-100 truncate">
                                {user.username}
                            </h3>
                            {user.subscriptionType && <Badge className={cn("!mt-1 !px-2 !py-0.5 !text-xs !font-medium !text-white !rounded-full !w-fit !border-none", getSubscriptionBadgeStyles(user.subscriptionType))}>
                                    {user.subscriptionType.toLowerCase().charAt(0).toUpperCase() + user.subscriptionType.toLowerCase().slice(1)}
                                </Badge>}
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="flex gap-4 md:gap-6 lg:gap-8 items-center py-2 border-t border-b border-slate-700/50">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1 text-sky-400">
                                <Users size={16} data-sentry-element="Users" data-sentry-source-file="index.tsx" />
                                <span className="text-xs md:text-base font-bold text-slate-200">
                                    {user.stats?.["Followers"] || 0}
                                </span>
                            </div>
                            <span className="text-xs text-slate-400">
                                Followers
                            </span>
                        </div>

                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1 text-rose-400">
                                <Heart size={16} data-sentry-element="Heart" data-sentry-source-file="index.tsx" />
                                <span className="text-xs md:text-base font-bold text-slate-200">
                                    {user.stats?.["Total Likes"] || 0}
                                </span>
                            </div>
                            <span className="text-xs text-slate-400">
                                Likes
                            </span>
                        </div>

                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1 text-indigo-400">
                                <ImageIcon size={16} data-sentry-element="ImageIcon" data-sentry-source-file="index.tsx" />
                                <span className="text-xs md:text-base font-bold text-slate-200">
                                    {user.stats?.["Images Generated"] || 0}
                                </span>
                            </div>
                            <span className="text-xs text-slate-400">
                                Images
                            </span>
                        </div>
                    </div>
                </CardContent>

                {/* Follow/Unfollow Button */}
                {user.username !== state.username && <CardFooter className="!p-2 md:!p-4 !pt-0">
                        <Button onClick={handleFollowToggle} className={cn("w-full", isFollowing ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-sky-600 hover:bg-sky-500 text-white")}>
                            {isFollowing ? <>
                                    <UserMinus size={16} />
                                    <span>Unfollow</span>
                                </> : <>
                                    <UserPlus size={16} />
                                    <span>Follow</span>
                                </>}
                        </Button>
                    </CardFooter>}
            </Card>
        </Link>;
};
export default UserCard;